import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import PropTypes from 'prop-types'

import { Navigate } from 'react-router-dom'
import { useIdleTimer } from 'react-idle-timer'

import {
  Loading,
  SignInRedirect,
  URLBuilder,
  isInIframe,
  useAccessTokenBootstrap,
  useGuestSessionFallback,
  useUserInfoQuery,
} from '@termly-web/common'

import ReactQueryStateHandler from 'components/ReactQueryStateHandler'

import { makeContext } from 'utils/contextFactory'

import noop from 'utils/noop'

import useFlowToken from '../../hooks/useFlowToken'

import pathMatches from './lib/pathMatches'
import useProactiveRefresh from './hooks/useProactiveRefresh'

const [AuthContext, useAuth] = makeContext('AuthContext')

const IDLE_TIMEOUT = 5 * 60 * 1_000


/**
 * @function useAuth()
 *
 * @returns {object} with the shape { user, excluded }
 */
export { useAuth }

/**
 * Provides the context for the `useAuth()` hook.
 *
 * @param {Object} props
 * @param {Boolean} [props.isGuestWelcome] - whether the pages below have a use
 *   for a guest, in which case a visitor the server refuses is given a guest
 *   identity rather than sent to sign in
 */
export default function AuthProvider({ children, handleAuthComplete = noop, isGuestWelcome }) {
  const [isTimedOut, setTimedOut] = useState()

  // Called ahead of the bootstrap, so that the token is gone from the address
  // before the bootstrap sends it anywhere.
  const flowToken = useFlowToken()

  const {
    isFlowTokenRejected,
    isReady: isAccessTokenReady,
  } = useAccessTokenBootstrap({
    flowToken,
  })

  const isUserInfoEnabled = ( isAccessTokenReady && !isFlowTokenRejected )

  // Mounted here rather than deeper because React Router holds this layout
  // route open across every navigation within its branch, so one timer covers
  // all of them. A page-level mount would arm a copy per page, each refreshing
  // on a schedule of its own.
  useProactiveRefresh()

  const {
    excluded,
    isLoadingError,
    isPending,
    isUnauthorized,
    refetch,
    user,
  } = useUserInfoQuery({
    enabled: isUserInfoEnabled,
  })

  const { isPending: isBootstrappingGuest } = useGuestSessionFallback({
    isGuestWelcome,
    isUnauthorized,
  })

  const isUnreadableUser = ( isLoadingError && !isUnauthorized )

  useEffect(() => {
    // useUserInfoQuery() will always return an object for `user`...but it'll only
    // have an `id` if it's a legitimate record.
    if ( !user.id ) {
      return
    }

    handleAuthComplete({
      user,
    })
  }, [handleAuthComplete, user])

  useIdleTimer({
    disabled: !user.integration_partner,
    onIdle() {
      setTimedOut(true)
    },
    timeout: IDLE_TIMEOUT,
  })

  const isEditor = ( user.is_collaborator && user.account_role === 'collaborating:editor' )

  /**
   * @function
   * @param {string} featureName
   * @returns {boolean}
   */
  const isExcludedFeature = useCallback((featureName) => {
    return new Set(excluded.features).has(featureName)
  }, [excluded])

  /**
   * @function
   * @param {string} locationPathname
   * @returns {boolean}
  */
  const isExcludedPathname = useCallback((locationPathname) => {
    return pathMatches(locationPathname, excluded.pages)
  }, [excluded])

  // The useMemo() may be a bit overkill, but I really want to avoid
  // causing an unnecessary full-on re-render of the page
  const value = useMemo(() => ({
    isEditor,
    isExcludedFeature,
    isExcludedPathname,
    refetch,
    user,
  }), [isEditor, isExcludedFeature, isExcludedPathname, refetch, user])

  // The token is checked alongside `isPending` because a record can already be
  // cached while no token is held. That settles `isPending` early, and the
  // tree below would then send its requests with no token.
  //
  // `isPending` rather than `isLoading`, which goes false for the render
  // between subscribing and the request going out, and the tree below reads the
  // record on its first render.
  //
  // A rejected flow token leaves the query disabled with no record, which React
  // Query reports as pending for good, so the wait ends with the bootstrap and
  // the session-expired page below can render.
  const isAwaitingUser = ( isUserInfoEnabled && isPending )

  if ( !isAccessTokenReady || isAwaitingUser ) {
    return null
  }

  // A refusal also holds the tree while a guest identity is being created to
  // answer it, so that the sign-in redirect below does not act on it first.
  // Creating one can take long enough to be mistaken for a broken page.
  if ( isBootstrappingGuest ) {
    return (
      <Loading
        data-testid="guest-session-loading"
      />
    )
  }

  // Inside an iframe, the partner that embeds the page is the only way back in,
  // so a guest ends up on the session-expired page rather than a sign-in form,
  // whether the server refused the credential or answered it with a new guest
  // session. A refusal has usually already started a full navigation there,
  // and sending somebody to sign in from here would overtake it.
  const isGuestInIframe = ( user.guest && isInIframe() )
  const isSessionExpired = ( isTimedOut || isFlowTokenRejected || isGuestInIframe )

  if ( isSessionExpired ) {
    const sessionExpiredURL = URLBuilder.sessionExpiredURL()

    return (
      <Navigate
        to={ sessionExpiredURL }
      />
    )
  }

  // Sent from here rather than from the page's own guard, because the pages
  // below fetch their own data first and would answer a refusal with an error
  // of their own before any guard among them ran.
  if ( isUnauthorized ) {
    return (
      <SignInRedirect />
    )
  }

  // Nothing below can tell somebody signed in from somebody who is not, so a
  // request that never got an answer stops here. Sending the person to sign in
  // instead would hand a login form to somebody whose session is fine, over a
  // blip that the next page load may well not repeat.
  if ( isUnreadableUser ) {
    return (
      <ReactQueryStateHandler
        isError
      />
    )
  }

  return (
    <AuthContext.Provider value={ value }>
      { children }
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.node,
  handleAuthComplete: PropTypes.func,
  isGuestWelcome: PropTypes.bool,
}

// Only for testing purposes
export const _private = { AuthContext }

